import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "stock-detail-container" }
const _hoisted_2 = {
  key: 0,
  class: "table-container"
}
const _hoisted_3 = { class: "product-info" }
const _hoisted_4 = { class: "text-icon" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = ["onClick"]

import MarketTypeTab from '@/components/holdings/MarketTypeTab.vue'
import TableEmpty from '@/components/holdings/TableEmpty.vue'
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { HoldingsMutationTypes } from '@/store/modules/holdings/mutations-type'
import { formatThousandsKeepDecimal } from '@/utils/num_format'
import { useRouter } from 'vue-router'
import useHolding from '@/hooks/holdings/useHolding'
import useFundAccMarket from '@/hooks/holdings/useFundAccMarket'
import useEnquireCurrency from '@/hooks/holdings/useEnquireCurrency'


export default /*@__PURE__*/_defineComponent({
  __name: 'StockDetail',
  setup(__props) {

const store = useStore()

const router = useRouter()

const baseMarketTypeList = [
  {
    label: '港股',
    value: 'HK'
  },
  {
    label: '美股',
    value: 'US'
  },
  {
    label: 'A股',
    value: 'A'
  },
  {
    label: '台股',
    value: 'TW'
  },
  {
    label: '日股',
    value: 'JP'
  },
  {
    label: '新加坡股',
    value: 'SG'
  }
]

const extraMarketType = {
  label: '其他股票',
  value: 'EXTRA'
}

const extraCurrencyActive = ref('HKD')
const extraCurrencyOptions = [
  { label: '港元', value: 'HKD' },
  { label: '美元', value: 'USD' }
]
const isShowExtraCurrencySelect = computed(() => store.state.holdings.marketTypeActive.value === 'EXTRA')


const shouldQueryFundAccMarketTypes = ['HK', 'US', 'A']
const onlyShowMarketValueTypes = ['TW', 'JP', 'SG', 'EXTRA']

const nonStockExchangeTypes = ['FUND', 'BOND', 'SPS', 'EXTRA']
const knownStockMarketTypes = ['HK', 'US', 'SZ', 'SH', 'TW', 'JP', 'SG']
const aShareExchangeTypes = ['SZ', 'SH', 'A']

const isMatchMarketType = (exchangeType: string, marketTypeValue: string) => {
  if (marketTypeValue === 'A') {
    return aShareExchangeTypes.includes(exchangeType)
  }
  return exchangeType === marketTypeValue
}

// 先过滤掉基金、债券、结构化票据和其他，仅保留股票类型数据
const normalizedStockRows = computed(() => {
  return (store.state.holdings.stockPosPageList || []).filter(item => !nonStockExchangeTypes.includes(item.exchangeType))
})

// 其他股票持仓列表
const extraStockRows = computed(() => {
  return normalizedStockRows.value.filter(item => !knownStockMarketTypes.includes(item.exchangeType))
})

// 港股、美股、A股 常驻显示
const alwaysShowMarketTypes = ['HK', 'US', 'A']

const marketTypeList = computed(() => {
  const visibleMarketTypes = baseMarketTypeList.filter(marketType => {
    if (alwaysShowMarketTypes.includes(marketType.value)) return true
    return normalizedStockRows.value.some(item => isMatchMarketType(item.exchangeType, marketType.value))
  })
  if (extraStockRows.value.length) {
    visibleMarketTypes.push(extraMarketType)
  }
  return visibleMarketTypes
})

// 持仓列表
const stockPosPageList = computed(() => {
  const marketTypeValue = store.state.holdings.marketTypeActive.value
  if (marketTypeValue === 'EXTRA') {
    return extraStockRows.value
  }
  return normalizedStockRows.value.filter(item => isMatchMarketType(item.exchangeType, marketTypeValue))
})

const getMarketValueFromRows = (marketTypeValue: string) => {
  const marketRows = marketTypeValue === 'EXTRA'
    ? extraStockRows.value
    : normalizedStockRows.value.filter(item => isMatchMarketType(item.exchangeType, marketTypeValue))
  if (!marketRows.length) {
    return null
  }
  return marketRows.reduce((sum, item) => {
    const value = marketTypeValue === 'EXTRA'
      ? item.marketValueHkd
      : Number(item.marketValue)
    return Number.isNaN(value) ? sum : sum + value
  }, 0)
}

const assetsInfo = computed(() => {
  const marketTypeValue = store.state.holdings.marketTypeActive.value

  if (onlyShowMarketValueTypes.includes(marketTypeValue)) {
    const marketValueLabel = marketTypeValue === 'EXTRA'
      ? `${store.state.holdings.marketTypeActive.label}总市值(${extraCurrencyActive.value})`
      : `${store.state.holdings.marketTypeActive.label}总市值`
    const rawMarketValue = getMarketValueFromRows(marketTypeValue)
    const marketValue = marketTypeValue === 'EXTRA' && rawMarketValue !== null
      ? convertCurrencyAmount(rawMarketValue, 'HKD', extraCurrencyActive.value)
      : rawMarketValue
    return [{
      label: marketValueLabel,
      value: marketValue === null ? '--' : formatThousandsKeepDecimal(marketValue, 2)
    }]
  }

  return [
    {
      label: store.state.holdings.marketTypeActive.label + '总市值',
      value: formatThousandsKeepDecimal(store.state.holdings.fundAccMarketList.marketValueDisplay, 2)
    },
    {
      label: '现金可提',
      value: formatThousandsKeepDecimal(store.state.holdings.fundAccMarketList.fetchBalanceDisplay, 2),
      icon: 'fetchBalanceDisplay'
    },
    {
      label: '最大购买力',
      value: formatThousandsKeepDecimal(store.state.holdings.fundAccMarketList.enableBalanceDisplay, 2),
      icon: 'enableBalanceDisplay'
    },
    {
      label: '冻结资金',
      value: formatThousandsKeepDecimal(store.state.holdings.fundAccMarketList.frozenBalanceDisplay, 2)
    }
  ]
})

const { queryHoldingData, loading } = useHolding({ exchangeType: store.state.holdings.accountTypeActive.value })
const { queryFundAccData } = useFundAccMarket()
const { convertCurrencyAmount } = useEnquireCurrency()

const formatTableMarketValue = (row: any) => {
  if (store.state.holdings.marketTypeActive.value !== 'EXTRA') {
    return row.marketValueDisplay
  }
  if (extraCurrencyActive.value === 'HKD') {
    return row.marketValueHkdDisplay
  }
  const convertedValue = convertCurrencyAmount(row.marketValueHkd, 'HKD', extraCurrencyActive.value)
  return convertedValue === null ? '--' : formatThousandsKeepDecimal(convertedValue, 2)
}

const formatTableCost = (row: any) => {
  if (store.state.holdings.marketTypeActive.value !== 'EXTRA') {
    return row.costPriceDisplay
  }
  const convertedCost = convertCurrencyAmount(row.costPriceDisplay, row.moneyType, extraCurrencyActive.value)
  return convertedCost === null ? '--' : formatThousandsKeepDecimal(convertedCost, 2)
}

const loadFundAccWhenNeeded = (marketTypeValue: string = store.state.holdings.marketTypeActive.value) => {
  if (shouldQueryFundAccMarketTypes.includes(marketTypeValue)) {
    queryFundAccData()
  }
}

watch(marketTypeList, (list) => {
  if (!list.length) return
  const activeValue = store.state.holdings.marketTypeActive.value
  if (!list.some(item => item.value === activeValue)) {
    const nextMarketType = list[0]
    store.commit(HoldingsMutationTypes.SET_MARKET_TYPE_ACTIVE, nextMarketType)
    loadFundAccWhenNeeded(nextMarketType.value)
  }
}, { immediate: true })

const handleBuy = (row: any) => {
  const { exchangeType, enableAmount, stockCode, stockNameGb }: any = row
  router.push({
    name: 'BulkTrading',
    params: {
      stockInfo: JSON.stringify({ exchangeType, enableAmount, stockCode, stockNameGb })
    }
  })
}

const handleSell = (row: any) => {
  const { exchangeType, enableAmount, stockCode, stockNameGb }: any = row
  router.push({
    name: 'BulkTrading',
    params: {
      stockInfo: JSON.stringify({ exchangeType, entrustAmount: enableAmount, stockCode, stockNameGb })
    }
  })
}

const formatIncomeBalanceColor = (incomeBalance: string) => {
  incomeBalance = incomeBalance?.replace(',', '')
  if (Number(incomeBalance) > 0) {
    return 'red'
  } else if (Number(incomeBalance) < 0) {
    return 'green'
  } else {
    return ''
  }
}

const tableMarketValueLabel = computed(() => {
  if (store.state.holdings.marketTypeActive.value === 'EXTRA') {
    return `总市值(${extraCurrencyActive.value})`
  }
  return '总市值'
})

const tableCostLabel = computed(() => {
  if (store.state.holdings.marketTypeActive.value === 'EXTRA') {
    return `成本(${extraCurrencyActive.value})`
  }
  return '成本'
})


onMounted(() => {
  queryHoldingData()
  loadFundAccWhenNeeded()
})


return (_ctx: any,_cache: any) => {
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_table = _resolveComponent("el-table")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(MarketTypeTab, {
      marketTypeList: marketTypeList.value,
      assetsInfo: assetsInfo.value,
      loadFunc: loadFundAccWhenNeeded
    }, {
      "tab-extra": _withCtx(() => [
        (isShowExtraCurrencySelect.value)
          ? (_openBlock(), _createBlock(_component_el_select, {
              key: 0,
              modelValue: extraCurrencyActive.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((extraCurrencyActive).value = $event)),
              style: {"width":"80px"}
            }, {
              default: _withCtx(() => [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(extraCurrencyOptions, (item) => {
                  return _createVNode(_component_el_option, {
                    key: item.value,
                    label: item.label,
                    value: item.value
                  }, null, 8, ["label", "value"])
                }), 64))
              ]),
              _: 1
            }, 8, ["modelValue"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["marketTypeList", "assetsInfo"]),
    _cache[2] || (_cache[2] = _createElementVNode("div", { class: "title" }, "持仓", -1)),
    (_unref(loading) || stockPosPageList.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _withDirectives((_openBlock(), _createBlock(_component_el_table, {
            data: stockPosPageList.value,
            "show-overflow-tooltip": "",
            "header-cell-class-name": "stock-detail-table-header-cell",
            "row-class-name": "stock-detail-table-row",
            style: {"width":"100%"},
            "max-height": "300"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_el_table_column, {
                prop: "stockCode",
                label: "代码"
              }),
              _createVNode(_component_el_table_column, {
                prop: "stockNameGb",
                label: "名称"
              }, {
                default: _withCtx(({ row }) => [
                  _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("span", null, _toDisplayString(row.stockNameGb || row.stockName), 1)
                  ])
                ]),
                _: 1
              }),
              _createVNode(_component_el_table_column, { label: tableMarketValueLabel.value }, {
                default: _withCtx((scope) => [
                  _createTextVNode(_toDisplayString(formatTableMarketValue(scope.row)), 1)
                ]),
                _: 1
              }, 8, ["label"]),
              (!['JP', 'SG', 'EXTRA'].includes(_unref(store).state.holdings.marketTypeActive.value))
                ? (_openBlock(), _createBlock(_component_el_table_column, {
                    key: 0,
                    prop: "enableAmountDisplay",
                    label: "可用"
                  }))
                : _createCommentVNode("", true),
              _createVNode(_component_el_table_column, {
                prop: "currentAmountDisplay",
                label: "持有"
              }),
              _createVNode(_component_el_table_column, { label: tableCostLabel.value }, {
                header: _withCtx(() => [
                  _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("span", null, _toDisplayString(tableCostLabel.value), 1)
                  ])
                ]),
                default: _withCtx((scope) => [
                  _createTextVNode(_toDisplayString(formatTableCost(scope.row)), 1)
                ]),
                _: 1
              }, 8, ["label"]),
              (['JP', 'SG'].includes(_unref(store).state.holdings.marketTypeActive.value))
                ? (_openBlock(), _createBlock(_component_el_table_column, {
                    key: 1,
                    prop: "closePrice",
                    label: "昨收"
                  }))
                : _createCommentVNode("", true),
              (['HK', 'US', 'A'].includes(_unref(store).state.holdings.marketTypeActive.value))
                ? (_openBlock(), _createBlock(_component_el_table_column, {
                    key: 2,
                    prop: "currentPriceDisplay",
                    label: "现价"
                  }))
                : _createCommentVNode("", true),
              (['HK', 'US', 'A'].includes(_unref(store).state.holdings.marketTypeActive.value))
                ? (_openBlock(), _createBlock(_component_el_table_column, {
                    key: 3,
                    label: "盈亏"
                  }, {
                    header: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                      _createElementVNode("div", { class: "text-icon" }, [
                        _createElementVNode("span", null, "盈亏")
                      ], -1)
                    ]))]),
                    default: _withCtx((scope) => [
                      _createElementVNode("div", {
                        class: _normalizeClass(formatIncomeBalanceColor(scope.row.incomeBalanceDisplay))
                      }, _toDisplayString(scope.row.incomeBalanceDisplay), 3),
                      _createElementVNode("div", {
                        class: _normalizeClass(formatIncomeBalanceColor(scope.row.incomeBalanceDisplay))
                      }, _toDisplayString(scope.row.incomePercentDisplay), 3)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (['HK', 'US', 'A', 'TW'].includes(_unref(store).state.holdings.marketTypeActive.value))
                ? (_openBlock(), _createBlock(_component_el_table_column, {
                    key: 4,
                    label: "操作",
                    width: "150",
                    fixed: "right"
                  }, {
                    default: _withCtx((scope) => [
                      _createElementVNode("div", null, [
                        _createElementVNode("div", {
                          class: "buy-btn",
                          onClick: ($event: any) => (handleBuy(scope.row))
                        }, "买入", 8, _hoisted_5),
                        _createElementVNode("div", {
                          class: "sell-btn",
                          onClick: ($event: any) => (handleSell(scope.row))
                        }, "卖出", 8, _hoisted_6)
                      ])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["data"])), [
            [_directive_loading, _unref(loading)]
          ])
        ]))
      : _createCommentVNode("", true),
    (!_unref(loading) && !stockPosPageList.value.length)
      ? (_openBlock(), _createBlock(TableEmpty, { key: 1 }))
      : _createCommentVNode("", true)
  ]))
}
}

})